import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserGroupIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  DocumentArrowUpIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { usePageTitle } from '../hooks/usePageTitle';
import { apiService, CreateUserGroupRequest, CreateContactRequest } from '../services/api';
import { toast } from 'react-hot-toast';


const ContactsGroups: React.FC = () => {
  // Set page title and description
  usePageTitle();
  
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'contacts' | 'groups'>('contacts');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [editingGroup, setEditingGroup] = useState<any>(null);
  const [showViewGroup, setShowViewGroup] = useState(false);
  const [viewingGroup, setViewingGroup] = useState<any>(null);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showEditContact, setShowEditContact] = useState(false);
  const [showDeleteContact, setShowDeleteContact] = useState(false);
  const [editingContact, setEditingContact] = useState<any>(null);
  const [deletingContact, setDeletingContact] = useState<any>(null);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvData, setCsvData] = useState<string>('');
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: ''
  });
  const [contactFormErrors, setContactFormErrors] = useState({
    name: '',
    email: ''
  });

  // Group form state
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: ''
  });
  const [selectedContacts, setSelectedContacts] = useState<any[]>([]);
  const [contactSearch, setContactSearch] = useState('');

  // Debug csvData changes
  useEffect(() => {
    console.log('🔄 State Change: csvData updated:', csvData);
    console.log('🔄 State Change: csvData length:', csvData.length);
    console.log('🔄 State Change: csvData.trim() length:', csvData.trim().length);
    console.log('🔄 State Change: Button should be enabled:', !!csvData.trim());
  }, [csvData]);

  // Fetch contacts
  const { data: contacts, isLoading: contactsLoading, error: contactsError } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => apiService.getContacts(),
    refetchInterval: 30000,
  });

  // Fetch user groups
  const { data: groupsResponse, isLoading: groupsLoading, error: groupsError } = useQuery({
    queryKey: ['user-groups'],
    queryFn: () => apiService.getUserGroups(),
    refetchInterval: 30000,
  });

  // Extract groups array from response and ensure it's always an array
  const safeGroups = groupsResponse?.groups && Array.isArray(groupsResponse.groups) ? groupsResponse.groups : [];

  // Create user group mutation
  const createGroupMutation = useMutation({
    mutationFn: (groupData: CreateUserGroupRequest) => apiService.createUserGroup(groupData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-groups'] });
      setShowAddGroup(false);
    },
  });

  // Update user group mutation
  const updateGroupMutation = useMutation({
    mutationFn: ({ groupId, groupData }: { groupId: string; groupData: any }) => 
      apiService.updateUserGroup(groupId, groupData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-groups'] });
      setShowEditGroup(false);
      setEditingGroup(null);
      setGroupForm({ name: '', description: '' });
      setSelectedContacts([]);
      setContactSearch('');
      toast.success('Group updated successfully!');
    },
    onError: (error: any) => {
      console.error('❌ API: Failed to update group:', error);
      toast.error('Failed to update group. Please try again.');
    },
  });

  // Create contact mutation
  const createContactMutation = useMutation({
    mutationFn: (contactData: CreateContactRequest) => {
      console.log('📤 API: Creating contact with data:', contactData);
      return apiService.createContact(contactData);
    },
    onSuccess: (data) => {
      console.log('✅ API: Contact created successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setShowAddContact(false);
      setContactForm({ name: '', email: '' });
      setContactFormErrors({ name: '', email: '' });
      toast.success('Contact created successfully!');
    },
    onError: (error: any) => {
      console.error('❌ API: Failed to create contact:', error);
      toast.error('Failed to create contact. Please try again.');
    },
  });

  // Update contact mutation
  const updateContactMutation = useMutation({
    mutationFn: ({ contactId, contactData }: { contactId: string; contactData: Partial<CreateContactRequest> }) => 
      apiService.updateContact(contactId, contactData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setShowEditContact(false);
      setEditingContact(null);
      setContactForm({ name: '', email: '' });
      setContactFormErrors({ name: '', email: '' });
      toast.success('Contact updated successfully!');
    },
    onError: (error: any) => {
      console.error('❌ API: Failed to update contact:', error);
      toast.error('Failed to update contact. Please try again.');
    },
  });

  // Delete contact mutation
  const deleteContactMutation = useMutation({
    mutationFn: (contactId: string) => apiService.deleteContact(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setShowDeleteContact(false);
      setDeletingContact(null);
      toast.success('Contact deleted successfully!');
    },
    onError: (error: any) => {
      console.error('❌ API: Failed to delete contact:', error);
      toast.error('Failed to delete contact. Please try again.');
    },
  });

  // Delete user group mutation
  const deleteGroupMutation = useMutation({
    mutationFn: (groupId: string) => apiService.deleteUserGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-groups'] });
    },
  });


  const handleAddGroup = (groupData: CreateUserGroupRequest) => {
    createGroupMutation.mutate(groupData);
  };

  const handleEditGroup = (group: any) => {
    setEditingGroup(group);
    setGroupForm({ name: group.name, description: group.description || '' });
    setSelectedContacts([]); // Will be populated with current members
    setContactSearch('');
    setShowEditGroup(true);
  };

  const handleViewGroup = (group: any) => {
    setViewingGroup(group);
    setShowViewGroup(true);
  };

  // Query to fetch group members
  const { data: groupMembers, isLoading: membersLoading, error: membersError } = useQuery({
    queryKey: ['groupMembers', viewingGroup?.id],
    queryFn: () => apiService.getUserGroupMembers(viewingGroup.id),
    enabled: !!viewingGroup?.id && showViewGroup,
  });

  // Match group members with contacts data
  const enrichedMembers = groupMembers?.map(member => {
    const contact = contacts?.find(c => c.id === member.contact_id);
    return {
      ...member,
      name: contact?.name || 'Unknown',
      email: contact?.email || 'No email'
    };
  }) || [];

  const handleEditGroupSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('🚀 Edit group form submitted!', groupForm, selectedContacts);
    
    updateGroupMutation.mutate({
      groupId: editingGroup.id,
      groupData: {
        name: groupForm.name,
        description: groupForm.description,
        members: selectedContacts.map(contact => contact.id) // Send contact IDs instead of emails
      }
    });
  };

  const handleDeleteGroup = (groupId: string) => {
    setGroupToDelete(groupId);
    setShowDeleteGroupModal(true);
  };

  const confirmDeleteGroup = () => {
    if (groupToDelete) {
      deleteGroupMutation.mutate(groupToDelete);
      setShowDeleteGroupModal(false);
      setGroupToDelete(null);
    }
  };

  const handleEditContact = (contact: any) => {
    setEditingContact(contact);
    setContactForm({ name: contact.name, email: contact.email });
    setContactFormErrors({ name: '', email: '' });
    setShowEditContact(true);
  };

  const handleDeleteContact = (contact: any) => {
    setDeletingContact(contact);
    setShowDeleteContact(true);
  };

  const handleEditContactSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('🚀 Edit form submitted!', contactForm);
    
    // Clear previous errors
    setContactFormErrors({ name: '', email: '' });
    
    let hasErrors = false;
    const errors = { name: '', email: '' };
    
    // Validate name
    if (!contactForm.name.trim()) {
      errors.name = 'Name is required';
      hasErrors = true;
    }
    
    // Validate email
    if (!contactForm.email.trim()) {
      errors.email = 'Email is required';
      hasErrors = true;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactForm.email)) {
        errors.email = 'Please enter a valid email address';
        hasErrors = true;
      }
    }
    
    if (hasErrors) {
      console.log('❌ Validation errors:', errors);
      setContactFormErrors(errors);
      return;
    }
    
    console.log('✅ Validation passed, calling API...');
    updateContactMutation.mutate({
      contactId: editingContact.id,
      contactData: contactForm
    });
  };

  const confirmDeleteContact = () => {
    if (deletingContact) {
      deleteContactMutation.mutate(deletingContact.id);
    }
  };

  // Contact selection handlers for group editing
  const toggleContactSelection = (contact: any) => {
    setSelectedContacts(prev => {
      const isSelected = prev.some(c => c.id === contact.id);
      if (isSelected) {
        return prev.filter(c => c.id !== contact.id);
      } else {
        return [...prev, contact];
      }
    });
  };

  const removeSelectedContact = (contactId: string) => {
    setSelectedContacts(prev => prev.filter(c => c.id !== contactId));
  };

  const handleAddContactSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log('🚀 Form submitted!', contactForm);
    
    // Clear previous errors
    setContactFormErrors({ name: '', email: '' });
    
    let hasErrors = false;
    const errors = { name: '', email: '' };
    
    // Validate name
    if (!contactForm.name.trim()) {
      errors.name = 'Name is required';
      hasErrors = true;
    }
    
    // Validate email
    if (!contactForm.email.trim()) {
      errors.email = 'Email is required';
      hasErrors = true;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contactForm.email)) {
        errors.email = 'Please enter a valid email address';
        hasErrors = true;
      }
    }
    
    if (hasErrors) {
      console.log('❌ Validation errors:', errors);
      setContactFormErrors(errors);
      return;
    }
    
    console.log('✅ Validation passed, calling API...');
    createContactMutation.mutate(contactForm);
  };

  const handleCsvImport = () => {
    console.log('📋 CSV Import: handleCsvImport called');
    console.log('📋 CSV Import: Current csvData:', csvData);
    console.log('📋 CSV Import: csvData length:', csvData.length);
    console.log('📋 CSV Import: csvData.trim():', csvData.trim());
    console.log('📋 CSV Import: csvData.trim() length:', csvData.trim().length);
    
    if (csvData.trim()) {
      console.log('📋 CSV Import: csvData is not empty, proceeding with validation');
      
      // Validate CSV format
      const lines = csvData.trim().split('\n');
      console.log('📋 CSV Import: Number of lines in CSV:', lines.length);
      console.log('📋 CSV Import: All lines:', lines);
      
      if (lines.length < 2) {
        console.error('❌ CSV Import: Validation failed - less than 2 lines');
        toast.error('CSV must have at least a header row and one data row');
        return;
      }
      
      const header = lines[0].toLowerCase().trim();
      console.log('📋 CSV Import: CSV Header:', header);
      console.log('📋 CSV Import: Expected header: "name,email"');
      
      if (header !== 'name,email') {
        console.error('❌ CSV Import: Validation failed - incorrect header');
        toast.error('CSV header must be exactly "name,email"');
        return;
      }
      
      console.log('✅ CSV Import: Header validation passed');
      
      // Parse and validate data rows
      const contacts = [];
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        console.log(`📋 CSV Import: Processing line ${i + 1}:`, line);
        
        if (!line) {
          console.log(`📋 CSV Import: Skipping empty line ${i + 1}`);
          continue; // Skip empty lines
        }
        
        const parts = line.split(',');
        console.log(`📋 CSV Import: Line ${i + 1} parts:`, parts);
        
        if (parts.length !== 2) {
          console.error(`❌ CSV Import: Validation failed - Row ${i + 1} has incorrect number of columns`);
          toast.error(`Row ${i + 1}: Each row must have exactly 2 columns (name,email)`);
          return;
        }
        
        const [name, email] = parts.map(p => p.trim());
        console.log(`📋 CSV Import: Parsed name: "${name}", email: "${email}"`);
        
        if (!name || !email) {
          console.error(`❌ CSV Import: Validation failed - Row ${i + 1} has empty name or email`);
          toast.error(`Row ${i + 1}: Name and email are required`);
          return;
        }
        
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          console.error(`❌ CSV Import: Validation failed - Row ${i + 1} has invalid email format`);
          toast.error(`Row ${i + 1}: Invalid email format`);
          return;
        }
        
        console.log(`✅ CSV Import: Line ${i + 1} validation passed`);
        contacts.push({ name, email });
      }
      
      console.log('📋 CSV Import: Total contacts parsed:', contacts.length);
      console.log('📋 CSV Import: Parsed contacts:', contacts);
      
      if (contacts.length === 0) {
        console.error('❌ CSV Import: No valid contacts found');
        toast.error('No valid contacts found in CSV');
        return;
      }
      
      console.log('✅ CSV Import: All validation passed, calling importContactsFromCsv');
      // Import contacts one by one
      importContactsFromCsv(contacts);
    } else {
      console.log('📋 CSV Import: csvData is empty, not proceeding with import');
    }
  };

  const importContactsFromCsv = async (contacts: Array<{name: string, email: string}>) => {
    console.log('📋 CSV Import: importContactsFromCsv called with contacts:', contacts);
    console.log('📋 CSV Import: Starting batch import for', contacts.length, 'contacts');
    
    let successCount = 0;
    let errorCount = 0;
    
    for (let i = 0; i < contacts.length; i++) {
      const contact = contacts[i];
      console.log(`📋 CSV Import: Processing contact ${i + 1}/${contacts.length}:`, contact);
      
      try {
        console.log(`📋 CSV Import: Calling apiService.createContact for:`, contact);
        await apiService.createContact(contact);
        successCount++;
        console.log(`✅ CSV Import: Successfully imported contact ${i + 1}:`, contact);
      } catch (error) {
        console.error(`❌ CSV Import: Failed to import contact ${i + 1}:`, contact, error);
        errorCount++;
      }
    }
    
    console.log('📋 CSV Import: Batch import completed. Successes:', successCount, 'Failures:', errorCount);
    
    // Refresh contacts list
    console.log('📋 CSV Import: Invalidating contacts query...');
    queryClient.invalidateQueries({ queryKey: ['contacts'] });
    
    // Close modal and show results
    console.log('📋 CSV Import: Closing modal and clearing data...');
    setShowCsvImport(false);
    setCsvData('');
    
    if (errorCount === 0) {
      console.log('✅ CSV Import: All contacts imported successfully');
      toast.success(`Successfully imported ${successCount} contacts!`);
    } else {
      console.log('⚠️ CSV Import: Some contacts failed to import');
      toast.error(`Imported ${successCount} contacts, ${errorCount} failed. Check console for details.`);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('📁 File Upload: handleFileUpload called');
    console.log('📁 File Upload: event.target.files:', event.target.files);
    
    const file = event.target.files?.[0];
    console.log('📁 File Upload: selected file:', file);
    
    if (file) {
      console.log('📁 File Upload: File details:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      });
      
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log('📁 File Upload: FileReader onload triggered');
        const content = e.target?.result as string;
        console.log('📁 File Upload: File content length:', content?.length);
        console.log('📁 File Upload: File content preview:', content?.substring(0, 200));
        console.log('📁 File Upload: Setting csvData state...');
        setCsvData(content);
        console.log('📁 File Upload: csvData state updated');
      };
      
      reader.onerror = (e) => {
        console.error('❌ File Upload: FileReader error:', e);
      };
      
      console.log('📁 File Upload: Starting to read file as text...');
      reader.readAsText(file);
    } else {
      console.log('📁 File Upload: No file selected');
    }
  };

  return (
    <div className="p-6">

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('contacts')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'contacts'
                  ? 'border-governate-500 text-governate-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Contacts
            </button>
            <button
              onClick={() => setActiveTab('groups')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'groups'
                  ? 'border-governate-500 text-governate-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              User Groups
            </button>
          </nav>
        </div>
      </div>

      {/* Action Buttons - Always visible */}
      <div className="mb-6">
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setShowAddContact(true)}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Contact
          </button>
          <button
            onClick={() => setShowCsvImport(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <DocumentArrowUpIcon className="w-5 h-5 mr-2" />
            Import CSV
          </button>
          <button
            onClick={() => setShowAddGroup(true)}
            className="bg-governate-600 text-white px-4 py-2 rounded-lg hover:bg-governate-700 transition-colors flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Create Group
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={activeTab === 'contacts' ? 'Search contacts...' : 'Search groups...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Content */}
      {activeTab === 'contacts' ? (
        <>
          {contactsLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-governate-500"></div>
            </div>
          ) : contactsError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">Error loading contacts: {contactsError.message}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Contacts</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {(() => {
                  const filteredContacts = contacts?.filter(contact => 
                    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    contact.email.toLowerCase().includes(searchTerm.toLowerCase())
                  ) || [];

                  if (filteredContacts.length === 0) {
                    return (
                      <div className="px-6 py-8 text-center text-gray-500">
                        <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No contacts yet</h3>
                        <p className="text-gray-500 mb-4">Add your first contact to get started</p>
                      </div>
                    );
                  }

                  return filteredContacts.map((contact) => (
                    <div key={contact.id} className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-governate-100 rounded-full flex items-center justify-center">
                            <span className="text-governate-600 font-medium text-sm">
                              {contact.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                          <div className="text-sm text-gray-500">{contact.email}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => handleEditContact(contact)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteContact(contact)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {groupsLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-governate-500"></div>
            </div>
          ) : groupsError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">Error loading groups: {groupsError.message}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">User Groups</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {(() => {
              const filteredGroups = safeGroups.filter(group => 
                group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                group.description?.toLowerCase().includes(searchTerm.toLowerCase())
              );
              
              if (filteredGroups.length === 0) {
                return (
                  <div className="px-6 py-12 text-center">
                    <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {safeGroups.length === 0 ? 'No user groups yet' : 'No groups match your search'}
                    </h3>
                    <p className="text-gray-500 mb-4">
                      {safeGroups.length === 0 
                        ? 'Create your first user group to get started with policy recipients.'
                        : 'Try adjusting your search terms.'
                      }
                    </p>
                    {safeGroups.length === 0 && (
                      <button
                        onClick={() => setShowAddGroup(true)}
                        className="bg-governate-600 text-white px-4 py-2 rounded-lg hover:bg-governate-700 transition-colors flex items-center mx-auto"
                      >
                        <PlusIcon className="w-5 h-5 mr-2" />
                        Create Your First Group
                      </button>
                    )}
                  </div>
                );
              }
              
              return filteredGroups.map((group) => (
              <div key={group.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <UserGroupIcon className="w-6 h-6 text-gray-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900">
                        {group.name}
                      </h4>
                      {group.description && (
                        <p className="text-sm text-gray-500 mt-1">{group.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {group.member_count} members • Created {new Date(group.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => handleViewGroup(group)}
                      className="text-gray-400 hover:text-blue-600"
                      title="View Group Members"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button 
                      onClick={() => handleEditGroup(group)}
                      className="text-gray-400 hover:text-gray-600"
                      title="Edit Group"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteGroup(group.id)}
                      className="text-gray-400 hover:text-red-600"
                      disabled={deleteGroupMutation.isPending}
                      title="Delete Group"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              ));
            })()}
          </div>
            </div>
          )}
        </>
      )}

      {/* Add Group Modal */}
      {showAddGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add New Group</h3>
              <button
                onClick={() => setShowAddGroup(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              handleAddGroup({
                name: formData.get('name') as string,
                description: formData.get('description') as string,
              });
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Group Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    name="description"
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddGroup(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createGroupMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-governate-600 rounded-lg hover:bg-governate-700 disabled:opacity-50"
                >
                  {createGroupMutation.isPending ? 'Creating...' : 'Add Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      {showEditGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Group</h3>
              <button
                onClick={() => {
                  setShowEditGroup(false);
                  setEditingGroup(null);
                  setGroupForm({ name: '', description: '' });
                  setSelectedContacts([]);
                  setContactSearch('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleEditGroupSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Group Details */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900">Group Details</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Group Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={groupForm.name}
                      onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      placeholder="Enter group name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      name="description"
                      value={groupForm.description}
                      onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      placeholder="Enter group description"
                    />
                  </div>
                </div>

                {/* Contact Selection */}
                <div className="space-y-4">
                  <h4 className="text-md font-medium text-gray-900">Group Members</h4>
                  
                  {/* Selected Contacts */}
                  {selectedContacts.length > 0 && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Selected Contacts</label>
                      <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2">
                        {selectedContacts.map((contact) => (
                          <div key={contact.id} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1 mb-1">
                            <span className="text-sm text-gray-700">{contact.name} ({contact.email})</span>
                            <button
                              type="button"
                              onClick={() => removeSelectedContact(contact.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Contact Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Add Contacts</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={contactSearch}
                        onChange={(e) => setContactSearch(e.target.value)}
                        placeholder="Search contacts..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      />
                    </div>
                    
                    {/* Contact List */}
                    <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                      {contacts?.filter(contact => 
                        contact.name.toLowerCase().includes(contactSearch.toLowerCase()) ||
                        contact.email.toLowerCase().includes(contactSearch.toLowerCase())
                      ).map((contact) => {
                        const isSelected = selectedContacts.some(c => c.id === contact.id);
                        return (
                          <div
                            key={contact.id}
                            onClick={() => toggleContactSelection(contact)}
                            className={`px-3 py-2 cursor-pointer hover:bg-gray-50 flex items-center justify-between ${
                              isSelected ? 'bg-governate-50 border-l-4 border-governate-500' : ''
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-governate-100 rounded-full flex items-center justify-center">
                                <span className="text-governate-600 font-medium text-sm">
                                  {contact.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                                <div className="text-xs text-gray-500">{contact.email}</div>
                              </div>
                            </div>
                            {isSelected && (
                              <div className="text-governate-600">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditGroup(false);
                    setEditingGroup(null);
                    setGroupForm({ name: '', description: '' });
                    setSelectedContacts([]);
                    setContactSearch('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateGroupMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-governate-600 rounded-lg hover:bg-governate-700 disabled:opacity-50"
                >
                  {updateGroupMutation.isPending ? 'Updating...' : 'Update Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Group Modal */}
      {showViewGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Group Members</h3>
              <button
                onClick={() => {
                  setShowViewGroup(false);
                  setViewingGroup(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">{viewingGroup?.name}</h4>
                {viewingGroup?.description && (
                  <p className="text-sm text-gray-600 mb-2">{viewingGroup.description}</p>
                )}
                <p className="text-xs text-gray-500">
                  {viewingGroup?.member_count || 0} members • Created {new Date(viewingGroup?.created_at).toLocaleDateString()}
                </p>
              </div>

              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Group Members</h4>
                {membersLoading ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-governate-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading members...</p>
                  </div>
                ) : membersError ? (
                  <div className="text-center py-8 text-red-500">
                    <p>Error loading members: {membersError.message}</p>
                  </div>
                ) : enrichedMembers && enrichedMembers.length > 0 ? (
                  <div className="space-y-2">
                    {enrichedMembers.map((member: any, index: number) => (
                      <div key={index} className="flex items-center space-x-3 bg-gray-50 rounded-lg p-3">
                        <div className="w-8 h-8 bg-governate-100 rounded-full flex items-center justify-center">
                          <span className="text-governate-600 font-medium text-sm">
                            {member.name ? member.name.charAt(0).toUpperCase() : '?'}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {member.name || 'Unknown'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {member.email || 'No email'}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400">
                          Added {new Date(member.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No members yet</h3>
                    <p className="text-gray-500">This group doesn't have any members.</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
              <button
                onClick={() => {
                  setShowViewGroup(false);
                  setViewingGroup(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showCsvImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Import Contacts from CSV</h3>
              <button
                onClick={() => setShowCsvImport(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Required CSV Format</h4>
                <p className="text-sm text-blue-700 mb-2">
                  Your CSV file must have exactly these headers in this order:
                </p>
                <div className="bg-white border border-blue-200 rounded p-2 font-mono text-sm">
                  <div className="text-blue-600">name,email</div>
                  <div className="text-gray-600">John Doe,john@example.com</div>
                  <div className="text-gray-600">Jane Smith,jane@example.com</div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload CSV File</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-governate-50 file:text-governate-700 hover:file:bg-governate-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  CSV format: name,email (one per line)
                </p>
              </div>

              {csvData && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CSV Preview</label>
                  <textarea
                    value={csvData}
                    onChange={(e) => setCsvData(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent font-mono text-sm"
                    placeholder="name,email&#10;John Doe,john@example.com&#10;Jane Smith,jane@example.com"
                  />
                </div>
              )}
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowCsvImport(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  console.log('🔘 Button Click: Import Contacts button clicked');
                  console.log('🔘 Button Click: csvData state:', csvData);
                  console.log('🔘 Button Click: csvData.trim():', csvData.trim());
                  console.log('🔘 Button Click: csvData.trim() length:', csvData.trim().length);
                  console.log('🔘 Button Click: Button disabled condition (!csvData.trim()):', !csvData.trim());
                  handleCsvImport();
                }}
                disabled={!csvData.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Import Contacts
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add New Contact</h3>
              <button
                onClick={() => setShowAddContact(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleAddContactSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent ${
                    contactFormErrors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter contact name"
                />
                {contactFormErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{contactFormErrors.name}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent ${
                    contactFormErrors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter email address"
                />
                {contactFormErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{contactFormErrors.email}</p>
                )}
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddContact(false);
                    setContactFormErrors({ name: '', email: '' });
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createContactMutation.isPending}
                  className="px-4 py-2 bg-governate-600 text-white rounded-lg hover:bg-governate-700 disabled:opacity-50 transition-colors"
                >
                  {createContactMutation.isPending ? 'Adding...' : 'Add Contact'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Contact Modal */}
      {showEditContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Contact</h3>
              <button
                onClick={() => {
                  setShowEditContact(false);
                  setEditingContact(null);
                  setContactForm({ name: '', email: '' });
                  setContactFormErrors({ name: '', email: '' });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleEditContactSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent ${
                    contactFormErrors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter contact name"
                />
                {contactFormErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{contactFormErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  required
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent ${
                    contactFormErrors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter email address"
                />
                {contactFormErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{contactFormErrors.email}</p>
                )}
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditContact(false);
                    setEditingContact(null);
                    setContactForm({ name: '', email: '' });
                    setContactFormErrors({ name: '', email: '' });
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateContactMutation.isPending}
                  className="px-4 py-2 bg-governate-600 text-white rounded-lg hover:bg-governate-700 disabled:opacity-50 transition-colors"
                >
                  {updateContactMutation.isPending ? 'Updating...' : 'Update Contact'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Contact Confirmation Modal */}
      {showDeleteContact && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Delete Contact</h3>
              <button
                onClick={() => {
                  setShowDeleteContact(false);
                  setDeletingContact(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-600">
                Are you sure you want to delete <strong>{deletingContact?.name}</strong>? 
                This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteContact(false);
                  setDeletingContact(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteContact}
                disabled={deleteContactMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleteContactMutation.isPending ? 'Deleting...' : 'Delete Contact'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Group Confirmation Modal */}
      {showDeleteGroupModal && groupToDelete && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
              onClick={() => !deleteGroupMutation.isPending && setShowDeleteGroupModal(false)}
            ></div>

            {/* Modal */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      Delete Group
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete this group? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={confirmDeleteGroup}
                  disabled={deleteGroupMutation.isPending}
                  className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {deleteGroupMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteGroupModal(false);
                    setGroupToDelete(null);
                  }}
                  disabled={deleteGroupMutation.isPending}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ContactsGroups;
